import type { PaymentEvidence, StaffPaymentDetailPayload } from "@lafayette/api-client";

// The staff payment detail as a screen reads it. The server sends the use cases' own records (a section is a plain record on the wire), so
// each field is read with a check and a missing or wrong-typed one reads as `null`: the screen shows a dash and never crashes, and it never
// invents a value. Money is `{ amountMinor, currency }` with an integer amount.

type Rec = Readonly<Record<string, unknown>>;

const text = (record: Rec, key: string): string | null => {
  const value = record[key];
  return typeof value === "string" ? value : null;
};
const int = (record: Rec, key: string): number | null => {
  const value = record[key];
  return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
};
const money = (record: Rec, key: string): Money | null => {
  const value = record[key];
  if (typeof value !== "object" || value === null) return null;
  const inner = value as Rec;
  const amountMinor = int(inner, "amountMinor");
  const currency = text(inner, "currency");
  return amountMinor === null || currency === null ? null : { amountMinor, currency };
};

export type Money = Readonly<{ amountMinor: number; currency: string }>;

export type RefundRow = Readonly<{
  id: string;
  amount: Money | null;
  status: string | null;
  reasonCode: string | null;
  requestedAt: string | null;
  settledAt: string | null;
  failedAt: string | null;
  providerRefundId: string | null;
}>;

export type ObservationRow = Readonly<{
  id: string | null;
  kind: string | null;
  decision: string | null;
  decisionCode: string | null;
  verification: string | null;
  observed: Money | null;
  observedAt: string | null;
  receivedAt: string | null;
  providerPaymentId: string | null;
}>;

export type ExceptionRow = Readonly<{
  id: string | null;
  kind: string | null;
  status: string | null;
  openedAt: string | null;
  resolvedAt: string | null;
  resolutionCode: string | null;
}>;

export type TransitionRow = Readonly<{
  version: number | null;
  from: string | null;
  to: string | null;
  actorKind: string | null;
  actorId: string | null;
  evidenceKind: string | null;
  /** A staff reason: staff only. */
  internalReason: string | null;
  occurredAt: string | null;
}>;

export type ProviderCallRow = Readonly<{
  id: string | null;
  kind: string | null;
  state: string | null;
  attempt: number | null;
  providerObjectId: string | null;
  outcomeCode: string | null;
  errorClass: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}>;

export type RunRow = Readonly<{
  id: string | null;
  scope: string | null;
  state: string | null;
  examined: number | null;
  applied: number | null;
  duplicates: number | null;
  exceptions: number | null;
  deferred: number | null;
  foreign: number | null;
  errorClass: string | null;
  startedAt: string | null;
}>;

export type TimelineEntry = Readonly<{
  key: string;
  at: string | null;
  kind: "observation" | "call" | "run";
  title: string;
  detail: string;
}>;

export type PaymentDetailModel = Readonly<{
  id: string;
  status: string | null;
  version: number | null;
  amount: Money | null;
  refundedAmountMinor: number;
  /** Money held by refunds the provider has not confirmed yet (status REQUESTED): a new refund cannot take it. */
  reservedRefundMinor: number;
  /**
   * What can still be refunded: only a PAID or PARTIALLY_REFUNDED payment has any; `0` otherwise. It is the amount minus the settled
   * refunds AND minus the refunds that are still waiting for the provider, because the server reserves their amount (`exceeds_paid`).
   */
  remainingRefundableMinor: number;
  description: string | null;
  sourceType: string | null;
  conciergeRequestId: string | null;
  retryOfPaymentRequestId: string | null;
  provider: string | null;
  providerOrderId: string | null;
  expiresAt: string | null;
  createdAt: string | null;
  statusChangedAt: string | null;
  paidAt: string | null;
  evidence: PaymentEvidence;
  quote: Readonly<{
    id: string | null;
    amount: Money | null;
    description: string | null;
    customerTerms: string | null;
    validUntil: string | null;
    status: string | null;
    approvalBasis: string | null;
    supplierEvidenceKind: string | null;
  }> | null;
  amountSource: Readonly<{
    sourceType: string | null;
    sourceId: string | null;
    sourceVersion: number | null;
    fingerprint: string | null;
    resolvedAt: string | null;
  }> | null;
  refunds: readonly RefundRow[];
  observations: readonly ObservationRow[];
  exceptions: readonly ExceptionRow[];
  openExceptions: number;
  transitions: readonly TransitionRow[];
  providerCalls: readonly ProviderCallRow[];
  runs: readonly RunRow[];
  hasRequestedRefund: boolean;
  hasOpenProviderCall: boolean;
  /** The provider holds something for this payment: an order, a call Lafayette made, or something it said. */
  hasProviderTrail: boolean;
}>;

export function readPaymentDetail(payload: StaffPaymentDetailPayload): PaymentDetailModel {
  const request = payload.request;
  const status = text(request, "status");
  const amount = money(request, "amount");
  const refunded = int(request, "refundedAmountMinor") ?? 0;
  const refundable = status === "PAID" || status === "PARTIALLY_REFUNDED";
  const refunds: RefundRow[] = payload.refunds.map((row) => ({
    id: text(row, "id") ?? "",
    amount: money(row, "amount"),
    status: text(row, "status"),
    reasonCode: text(row, "reasonCode"),
    requestedAt: text(row, "requestedAt"),
    settledAt: text(row, "settledAt"),
    failedAt: text(row, "failedAt"),
    providerRefundId: text(row, "providerRefundId"),
  }));
  // A REQUESTED refund reserves its amount until the provider settles or refuses it; a refund whose amount cannot be read reserves nothing
  // here (the server still bounds the amount).
  const reserved = refunds.reduce(
    (sum, row) => (row.status === "REQUESTED" ? sum + (row.amount?.amountMinor ?? 0) : sum),
    0,
  );
  const providerCalls: ProviderCallRow[] = payload.providerCalls.map((row) => ({
    id: text(row, "id"),
    kind: text(row, "kind"),
    state: text(row, "state"),
    attempt: int(row, "attempt"),
    providerObjectId: text(row, "providerObjectId"),
    outcomeCode: text(row, "outcomeCode"),
    errorClass: text(row, "errorClass"),
    startedAt: text(row, "startedAt"),
    finishedAt: text(row, "finishedAt"),
  }));
  const exceptions: ExceptionRow[] = payload.exceptions.map((row) => ({
    id: text(row, "id"),
    kind: text(row, "kind"),
    status: text(row, "status"),
    openedAt: text(row, "openedAt"),
    resolvedAt: text(row, "resolvedAt"),
    resolutionCode: text(row, "resolutionCode"),
  }));
  const quote = payload.quote;
  const source = payload.amountSource;
  return {
    id: text(request, "id") ?? "",
    status,
    version: int(request, "version"),
    amount,
    refundedAmountMinor: refunded,
    reservedRefundMinor: reserved,
    remainingRefundableMinor:
      refundable && amount !== null ? Math.max(0, amount.amountMinor - refunded - reserved) : 0,
    description: text(request, "description"),
    sourceType: text(request, "sourceType"),
    conciergeRequestId: text(request, "conciergeRequestId"),
    retryOfPaymentRequestId: text(request, "retryOfPaymentRequestId"),
    provider: text(request, "provider"),
    providerOrderId: text(request, "providerOrderId"),
    expiresAt: text(request, "expiresAt"),
    createdAt: text(request, "createdAt"),
    statusChangedAt: text(request, "statusChangedAt"),
    paidAt: text(request, "paidAt"),
    evidence: payload.evidence,
    quote:
      quote === null
        ? null
        : {
            id: text(quote, "id"),
            amount: money(quote, "amount"),
            description: text(quote, "description"),
            customerTerms: text(quote, "customerTerms"),
            validUntil: text(quote, "validUntil"),
            status: text(quote, "status"),
            approvalBasis: text(quote, "approvalBasis"),
            supplierEvidenceKind: text(quote, "supplierEvidenceKind"),
          },
    amountSource:
      source === null
        ? null
        : {
            sourceType: text(source, "sourceType"),
            sourceId: text(source, "sourceId"),
            sourceVersion: int(source, "sourceVersion"),
            fingerprint: text(source, "fingerprint"),
            resolvedAt: text(source, "resolvedAt"),
          },
    refunds,
    observations: payload.observations.map((row) => ({
      id: text(row, "id"),
      kind: text(row, "kind"),
      decision: text(row, "decision"),
      decisionCode: text(row, "decisionCode"),
      verification: text(row, "signatureVerification"),
      observed: money(row, "observed"),
      observedAt: text(row, "observedAt"),
      receivedAt: text(row, "receivedAt"),
      providerPaymentId: text(row, "providerPaymentId"),
    })),
    exceptions,
    openExceptions: exceptions.filter((row) => row.status === "OPEN").length,
    transitions: payload.transitions.map((row) => ({
      version: int(row, "version"),
      from: text(row, "fromStatus"),
      to: text(row, "toStatus"),
      actorKind: text(row, "actorKind"),
      actorId: text(row, "actorId"),
      evidenceKind: text(row, "evidenceKind"),
      internalReason: text(row, "internalReason"),
      occurredAt: text(row, "occurredAt"),
    })),
    providerCalls,
    runs: payload.reconciliationRuns.map((row) => ({
      id: text(row, "id"),
      scope: text(row, "scope"),
      state: text(row, "state"),
      examined: int(row, "examined"),
      applied: int(row, "applied"),
      duplicates: int(row, "duplicates"),
      exceptions: int(row, "exceptions"),
      deferred: int(row, "deferred"),
      foreign: int(row, "foreign"),
      errorClass: text(row, "errorClass"),
      startedAt: text(row, "startedAt"),
    })),
    hasRequestedRefund: refunds.some((row) => row.status === "REQUESTED"),
    hasOpenProviderCall: providerCalls.some(
      (row) => row.state === "CALLING" || row.state === "AMBIGUOUS",
    ),
    hasProviderTrail:
      text(request, "providerOrderId") !== null ||
      providerCalls.length > 0 ||
      payload.observations.length > 0,
  };
}

const time = (value: string | null): number => {
  const parsed = value === null ? Number.NaN : new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The provider evidence in time order, oldest first: every observation (what the provider said, how it was authenticated and what
 * Lafayette decided), every provider call (what Lafayette asked, with its outcome) and every reconcile run. `titles` give the words.
 */
export function buildEvidenceTimeline(
  model: PaymentDetailModel,
  words: Readonly<{
    observationKind: (code: string | null) => string;
    decision: (code: string | null) => string;
    verification: (code: string | null) => string;
    callKind: (code: string | null) => string;
    callState: (code: string | null) => string;
    errorClass: (code: string | null) => string;
    runScope: (code: string | null) => string;
    runState: (code: string | null) => string;
    money: (value: Money | null) => string;
  }>,
): readonly TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  model.observations.forEach((row, index) => {
    entries.push({
      key: `observation-${row.id ?? index}`,
      at: row.observedAt ?? row.receivedAt,
      kind: "observation",
      title: words.observationKind(row.kind),
      detail: [
        words.verification(row.verification),
        words.decision(row.decision),
        row.observed === null ? null : `Amount seen: ${words.money(row.observed)}`,
      ]
        .filter((part): part is string => part !== null)
        .join(" · "),
    });
  });
  model.providerCalls.forEach((row, index) => {
    entries.push({
      key: `call-${row.id ?? index}`,
      at: row.startedAt,
      kind: "call",
      title: `${words.callKind(row.kind)} (try ${row.attempt ?? "?"})`,
      detail: [
        words.callState(row.state),
        row.errorClass === null ? null : `Error ${words.errorClass(row.errorClass)}`,
        row.outcomeCode === null ? null : `Code ${row.outcomeCode}`,
      ]
        .filter((part): part is string => part !== null)
        .join(" · "),
    });
  });
  model.runs.forEach((row, index) => {
    const counts = [
      `${row.examined ?? 0} looked at`,
      `${row.applied ?? 0} applied`,
      `${row.duplicates ?? 0} duplicates`,
      `${row.exceptions ?? 0} exceptions`,
      `${row.deferred ?? 0} deferred`,
    ].join(", ");
    entries.push({
      key: `run-${row.id ?? index}`,
      at: row.startedAt,
      kind: "run",
      title: words.runScope(row.scope),
      detail: `${words.runState(row.state)} · ${counts}`,
    });
  });
  return entries.sort((a, b) => time(a.at) - time(b.at));
}
